import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Slider } from '../../ui/slider';
import { Alert, AlertDescription } from '../../ui/alert';
import { CheckCircle, Info, Plus, Trash2, AlertTriangle, RefreshCw, Lock } from 'lucide-react';
import { 
  PSFS_CONFIG, 
  validatePSFSActivities, 
  calculatePSFSScore,
  getPSFSScoreColor,
  formatPSFSScore
} from './psfsSchema';

/**
 * PSFSQuestionnaire - Cuestionario PSFS (Patient-Specific Functional Scale)
 * 
 * Props:
 * - onComplete: callback con los resultados
 * - baselineActivities: actividades del baseline para precargar en seguimientos
 * - isFollowup: si es un seguimiento (precargar actividades)
 * - config: configuración personalizada
 * - allowEditActivities: permitir editar actividades en seguimiento
 */
const PSFSQuestionnaire = ({ 
  onComplete, 
  baselineActivities = null,
  isFollowup = false,
  config = PSFS_CONFIG,
  allowEditActivities = false
}) => {
  // Estado para las actividades
  const [activities, setActivities] = useState(() => {
    // Si hay baseline y es followup, precargar las actividades
    if (isFollowup && baselineActivities && baselineActivities.length > 0) {
      return baselineActivities.map(a => ({
        label: a.label,
        score_0_10: null, // Score vacío para que el paciente lo llene
        baselineScore: a.score_0_10 // Guardar el score baseline para comparar
      }));
    }
    // Si no, crear actividades vacías
    return Array.from({ length: config.defaultActivities }, () => ({
      label: '',
      score_0_10: null,
      baselineScore: null
    }));
  });

  const [errors, setErrors] = useState([]);
  const [editingMode, setEditingMode] = useState(!isFollowup || !baselineActivities);
  const [showEditWarning, setShowEditWarning] = useState(false);

  // Manejar cambio de actividad
  const handleActivityLabelChange = (index, value) => {
    if (!editingMode && isFollowup) return;
    setActivities(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], label: value };
      return updated;
    });
    setErrors([]);
  };

  // Manejar cambio de puntuación
  const handleScoreChange = (index, value) => {
    setActivities(prev => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        score_0_10: config.allowDecimals ? value : Math.round(value)
      };
      return updated;
    });
    setErrors([]);
  };

  // Añadir actividad
  const handleAddActivity = () => {
    if (activities.length >= config.maxActivities) return;
    setActivities(prev => [...prev, { label: '', score_0_10: null, baselineScore: null }]);
  };

  // Eliminar actividad
  const handleRemoveActivity = (index) => {
    if (activities.length <= config.minActivities) return;
    setActivities(prev => prev.filter((_, i) => i !== index));
  };

  // Confirmar edición de actividades en seguimiento
  const handleConfirmEdit = () => {
    setEditingMode(true);
    setShowEditWarning(false);
  };

  // Manejar submit
  const handleSubmit = () => {
    const validation = validatePSFSActivities(activities, config);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    setErrors([]);
    
    // Preparar actividades para guardar
    const activitiesToSave = activities
      .filter(a => a.label && a.label.trim() !== '')
      .map(a => ({
        label: a.label.trim(),
        score_0_10: a.score_0_10,
        baselineScore: a.baselineScore
      }));
    
    const scores = calculatePSFSScore(activitiesToSave);
    
    onComplete({
      activities: activitiesToSave,
      ...scores,
      questionnaire_type: 'PSFS',
      version: config.version,
      is_followup: isFollowup,
      completed_at: new Date().toISOString()
    });
  };

  // Calcular preview de scores
  const previewScores = calculatePSFSScore(activities);

  // Calcular actividades con label
  const activitiesWithLabel = activities.filter(a => a.label && a.label.trim() !== '');
  const completedActivities = activities.filter(a => 
    a.label && a.label.trim() !== '' && a.score_0_10 !== null
  );

  return (
    <div className="space-y-6">
      {/* Cabecera con instrucciones */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-teal-900">{config.texts.title}</h3>
            <p className="text-sm text-teal-700 mt-1">
              {isFollowup && baselineActivities ? config.texts.instructionsFollowup : config.texts.instructions}
            </p>
          </div>
        </div>
      </div>

      {/* Advertencia si es seguimiento y las actividades están bloqueadas */}
      {isFollowup && baselineActivities && !editingMode && (
        <Alert className="bg-blue-50 border-blue-200">
          <Lock className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            <p className="font-medium">Modo Seguimiento</p>
            <p className="text-sm mt-1">
              Las actividades se han precargado de su evaluación anterior para comparar la evolución.
              {allowEditActivities && (
                <Button
                  variant="link"
                  className="text-blue-700 underline p-0 h-auto ml-1"
                  onClick={() => setShowEditWarning(true)}
                >
                  ¿Desea modificarlas?
                </Button>
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Modal de confirmación para editar */}
      {showEditWarning && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-700">
            <p className="font-medium">{config.texts.editActivitiesWarning}</p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEditWarning(false)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleConfirmEdit}
              >
                Sí, modificar
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Progreso */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {completedActivities.length} de {Math.max(activitiesWithLabel.length, config.minActivities)} actividades completadas
        </span>
        {previewScores.mean_score !== null && (
          <span className="font-medium" style={{ color: getPSFSScoreColor(previewScores.mean_score) }}>
            Media: {formatPSFSScore(previewScores.mean_score)}/10
          </span>
        )}
      </div>

      {/* Actividades */}
      <div className="space-y-4">
        {activities.map((activity, index) => (
          <Card 
            key={index}
            className={`border-2 transition-colors ${
              activity.label && activity.score_0_10 !== null
                ? 'border-teal-200 bg-teal-50/30' 
                : 'border-gray-200'
            }`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    activity.label && activity.score_0_10 !== null
                      ? 'bg-teal-500 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {activity.label && activity.score_0_10 !== null 
                      ? <CheckCircle className="w-4 h-4" /> 
                      : index + 1}
                  </span>
                  <span className="text-gray-900">Actividad {index + 1}</span>
                </div>
                {editingMode && activities.length > config.minActivities && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveActivity(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campo de actividad */}
              <div>
                <label className="text-sm text-gray-600 mb-1 block">
                  ¿Qué actividad le resulta difícil o no puede realizar?
                </label>
                <Input
                  value={activity.label}
                  onChange={(e) => handleActivityLabelChange(index, e.target.value)}
                  placeholder={config.texts.activityPlaceholder}
                  disabled={!editingMode && isFollowup}
                  className={!editingMode && isFollowup ? 'bg-gray-100' : ''}
                />
                {activity.baselineScore !== null && (
                  <p className="text-xs text-gray-500 mt-1">
                    Puntuación anterior: {activity.baselineScore}/10
                  </p>
                )}
              </div>

              {/* Slider de puntuación */}
              {activity.label && activity.label.trim() !== '' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-600">
                      ¿Cómo puede realizar esta actividad HOY?
                    </label>
                    <span 
                      className="text-2xl font-bold"
                      style={{ color: getPSFSScoreColor(activity.score_0_10) }}
                    >
                      {activity.score_0_10 !== null ? activity.score_0_10 : '—'}
                      <span className="text-sm text-gray-400">/10</span>
                    </span>
                  </div>
                  
                  <div className="px-2">
                    <Slider
                      value={[activity.score_0_10 ?? 5]}
                      onValueChange={([value]) => handleScoreChange(index, value)}
                      min={config.minScore}
                      max={config.maxScore}
                      step={config.allowDecimals ? 0.5 : 1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{config.texts.scoreMin}</span>
                      <span>{config.texts.scoreMax}</span>
                    </div>
                  </div>

                  {/* Comparación con baseline */}
                  {activity.baselineScore !== null && activity.score_0_10 !== null && (
                    <div className="flex items-center gap-2 text-sm">
                      <RefreshCw className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        Cambio desde baseline: 
                        <span 
                          className={`ml-1 font-medium ${
                            activity.score_0_10 > activity.baselineScore 
                              ? 'text-green-600' 
                              : activity.score_0_10 < activity.baselineScore 
                                ? 'text-red-600' 
                                : 'text-gray-600'
                          }`}
                        >
                          {activity.score_0_10 > activity.baselineScore ? '+' : ''}
                          {(activity.score_0_10 - activity.baselineScore).toFixed(1)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Botón añadir actividad */}
      {editingMode && activities.length < config.maxActivities && (
        <Button
          variant="outline"
          onClick={handleAddActivity}
          className="w-full border-dashed border-2"
        >
          <Plus className="w-4 h-4 mr-2 text-gray-600" />
          {config.texts.addActivity} ({activities.length}/{config.maxActivities})
        </Button>
      )}

      {/* Preview de resultados */}
      {previewScores.activities_count > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Resumen</h4>
            <div className="text-right">
              <p 
                className="text-3xl font-bold"
                style={{ color: getPSFSScoreColor(previewScores.mean_score) }}
              >
                {formatPSFSScore(previewScores.mean_score)}
              </p>
              <p className="text-xs text-gray-500">Media de {previewScores.activities_count} actividades</p>
            </div>
          </div>

          {/* Barra visual */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Incapacidad</span>
              <span>Sin limitación</span>
            </div>
            <div className="h-3 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-full relative">
              {previewScores.mean_score !== null && (
                <div 
                  className="absolute w-4 h-4 bg-white border-2 border-gray-700 rounded-full -top-0.5"
                  style={{ 
                    left: `${(previewScores.mean_score / 10) * 100}%`, 
                    transform: 'translateX(-50%)' 
                  }}
                />
              )}
            </div>
          </div>

          {previewScores.interpretation && (
            <p className="text-sm text-center text-gray-600">
              <strong>Interpretación:</strong> {previewScores.interpretation}
            </p>
          )}

          {/* Detalle por actividad */}
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase">Detalle por actividad</p>
            {activities.filter(a => a.label && a.label.trim() !== '').map((activity, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 truncate flex-1 mr-2">{activity.label}</span>
                <span 
                  className="font-medium"
                  style={{ color: getPSFSScoreColor(activity.score_0_10) }}
                >
                  {activity.score_0_10 !== null ? activity.score_0_10 : '—'}/10
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errores */}
      {errors.length > 0 && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">
            <ul className="list-disc list-inside">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Botón guardar */}
      <div className="flex justify-end pt-4 border-t">
        <Button
          onClick={handleSubmit}
          className="bg-teal-600 hover:bg-teal-700"
          disabled={completedActivities.length < config.minActivities}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Guardar Cuestionario
        </Button>
      </div>
    </div>
  );
};

export default PSFSQuestionnaire;
